import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/sections";
import { toast, Toaster } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, QrCode } from "lucide-react";
import EventQrModal from "@/components/EventQrModal";

const EMPTY = {
  title: "", slug: "", category: "workshop", section: "art-centre",
  short_description: "", description: "", instructor: "", image_url: "",
  price_inr: 0, capacity: 20,
  starts_at: "", ends_at: "",
  location: "Carpe Diem, Majorda, Goa",
  is_recurring: false, recurrence_label: "",
  booking_mode: "one_off", recurrence_weekdays: [], recurrence_time: "", session_count: 1,
  resource_pool: "",
  is_published: true,
  is_featured: false,
  gallery_images: [], catalog_url: "", catalog_filename: "",
};

const SECTIONS = ["art-gallery","art-centre","ceramic-studio","cafe","stage","coworking"];
const CATEGORIES = ["workshop","exhibition","performance","class"];
const WEEKDAY_OPTS = [
  { v: 0, l: "Monday" }, { v: 1, l: "Tuesday" }, { v: 2, l: "Wednesday" },
  { v: 3, l: "Thursday" }, { v: 4, l: "Friday" }, { v: 5, l: "Saturday" }, { v: 6, l: "Sunday" },
];

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingCatalog, setUploadingCatalog] = useState(false);
  const [pools, setPools] = useState([]);
  const [qrEvent, setQrEvent] = useState(null);

  useEffect(() => {
    api.get("/admin/resource-pools").then(r => setPools(r.data.pools || [])).catch(() => {});
  }, []);

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "event");
    setUploading(true);
    try {
      const { data } = await api.post("/gallery/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally { setUploading(false); }
  };

  const uploadGalleryImage = async (file) => {
    if (!file) return;
    if ((form.gallery_images || []).length >= 10) { toast.error("Maximum 10 gallery images per event"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "event-gallery");
    setUploadingGallery(true);
    try {
      const { data } = await api.post("/gallery/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, gallery_images: [...(f.gallery_images || []), { url: data.url, caption: "" }] }));
      toast.success("Image added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally { setUploadingGallery(false); }
  };

  const uploadCatalog = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "event-catalog");
    setUploadingCatalog(true);
    try {
      const { data } = await api.post("/gallery/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, catalog_url: data.url, catalog_filename: data.filename || "Catalogue" }));
      toast.success("Catalogue uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally { setUploadingCatalog(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/events").then(r => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (e) => {
    // Backfill new multi-weekday field from legacy single-weekday if needed
    const wd = e.recurrence_weekdays && e.recurrence_weekdays.length
      ? e.recurrence_weekdays
      : (e.recurrence_weekday != null ? [e.recurrence_weekday] : []);
    // Normalise gallery images: legacy events store plain URL strings
    const gi = (e.gallery_images || []).map(g => (typeof g === "string" ? { url: g, caption: "" } : g));
    setForm({ ...EMPTY, ...e, recurrence_weekdays: wd, gallery_images: gi });
    setEditingId(e.id);
    setOpen(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    try {
      const payload = {
        ...form,
        price_inr: Number(form.price_inr) || 0,
        capacity: Number(form.capacity) || 0,
      };
      if (editingId) {
        await api.put(`/admin/events/${editingId}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Event created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-events">
      <Toaster position="top-center" richColors />
      <div className="flex items-end justify-between">
        <div>
          <div className="overline">Programme</div>
          <h1 className="display text-5xl mt-3">Events.</h1>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="admin-event-create"><Plus size={14}/> New event</button>
      </div>

      <div className="mt-10 border border-soft bg-white">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-muted2">
            <tr>
              <th className="p-4">Title</th>
              <th className="p-4">Section</th>
              <th className="p-4">Date</th>
              <th className="p-4">Price</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="p-8 text-muted2">Loading…</td></tr>}
            {!loading && events.length === 0 && <tr><td colSpan="6" className="p-8 text-muted2">No events yet.</td></tr>}
            {events.map((e) => (
              <tr key={e.id} className="border-t border-soft" data-testid={`admin-event-row-${e.slug}`}>
                <td className="p-4">
                  <div className="font-serif text-lg">{e.title}</div>
                  <div className="text-xs text-muted2">{e.slug}</div>
                </td>
                <td className="p-4 text-xs">{e.section}<br/><span className="text-muted2">{e.category}</span></td>
                <td className="p-4 text-xs">{e.is_recurring ? e.recurrence_label : formatDate(e.starts_at)}</td>
                <td className="p-4 text-xs">{e.price_inr > 0 ? formatINR(e.price_inr) : "Free"}</td>
                <td className="p-4 text-xs">
                  {e.is_published ? <span className="text-sage" style={{color:"#4A5D4E"}}>Live</span> : <span className="text-muted2">Draft</span>}
                  {e.is_featured && <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] tracking-widest uppercase" style={{background:"#D4A373",color:"#1C1C19"}} data-testid={`admin-event-featured-${e.slug}`}>Highlight</span>}
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => setQrEvent(e)} className="mr-3 text-muted2 hover:text-charcoal" title="QR code for poster" data-testid={`admin-event-qr-${e.slug}`}><QrCode size={16}/></button>
                  <button onClick={() => openEdit(e)} className="mr-3 text-muted2 hover:text-charcoal" data-testid={`admin-event-edit-${e.slug}`}><Pencil size={16}/></button>
                  <button onClick={() => del(e.id)} className="text-muted2 hover:text-red-700" data-testid={`admin-event-delete-${e.slug}`}><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-auto p-6" onClick={() => setOpen(false)}>
          <div className="bg-cream max-w-3xl w-full border border-soft mt-10 mb-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-soft">
              <div className="font-serif text-2xl">{editingId ? "Edit event" : "New event"}</div>
              <button onClick={() => setOpen(false)} data-testid="admin-event-close"><X size={20}/></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-testid="admin-event-form">
              <input required placeholder="Title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="border-b border-soft py-2 bg-transparent" data-testid="event-form-title"/>
              <input required placeholder="Slug (url-friendly)" value={form.slug} onChange={(e)=>setForm({...form,slug:e.target.value})} className="border-b border-soft py-2 bg-transparent" data-testid="event-form-slug"/>
              <select value={form.section} onChange={(e)=>setForm({...form,section:e.target.value})} className="border-b border-soft py-2 bg-transparent" data-testid="event-form-section">
                {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} className="border-b border-soft py-2 bg-transparent" data-testid="event-form-category">
                {CATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <input placeholder="Instructor (optional)" value={form.instructor} onChange={(e)=>setForm({...form,instructor:e.target.value})} className="border-b border-soft py-2 bg-transparent"/>

              {/* Image — upload OR paste a URL. Upload uses our storage bucket. */}
              <div className="md:col-span-2">
                <div className="overline mb-2">Cover image</div>
                <div className="flex items-start gap-4">
                  <div className="w-28 h-28 border border-soft bg-cream-2 overflow-hidden shrink-0 flex items-center justify-center">
                    {form.image_url ? (
                      <img src={form.image_url.startsWith("http") ? form.image_url : (process.env.REACT_APP_BACKEND_URL + form.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs text-muted2 text-center px-2">No image</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="btn-ghost inline-flex items-center gap-2 cursor-pointer text-xs" data-testid="event-form-image-upload-btn">
                      <Upload size={12} /> {uploading ? "Uploading…" : "Upload image"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} data-testid="event-form-image-input"/>
                    </label>
                    <input
                      placeholder="…or paste an image URL"
                      value={form.image_url}
                      onChange={(e)=>setForm({...form,image_url:e.target.value})}
                      className="w-full border-b border-soft py-2 bg-transparent text-xs"
                      data-testid="event-form-image-url"
                    />
                  </div>
                </div>
              </div>

              {/* Extra gallery images (up to 10) — shown on the event page as clickable thumbnails */}
              <div className="md:col-span-2">
                <div className="overline mb-2">Gallery images ({(form.gallery_images || []).length}/10)</div>
                <div className="flex flex-wrap gap-3" data-testid="event-form-gallery-list">
                  {(form.gallery_images || []).map((img, i) => (
                    <div key={i} className="flex flex-col gap-1 w-36">
                      <div className="relative h-20 border border-soft bg-cream-2 overflow-hidden">
                        <img src={img.url.startsWith("http") ? img.url : (process.env.REACT_APP_BACKEND_URL + img.url)} alt="" className="h-full w-auto max-w-full object-contain block" />
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, gallery_images: f.gallery_images.filter((_, j) => j !== i) }))}
                          className="absolute top-1 right-1 bg-black/60 text-white p-0.5 hover:bg-black/80"
                          data-testid={`event-form-gallery-remove-${i}`}
                          title="Remove"
                        ><X size={12}/></button>
                      </div>
                      <input
                        placeholder="Caption (optional)"
                        value={img.caption || ""}
                        onChange={(e) => setForm(f => {
                          const gi = [...f.gallery_images];
                          gi[i] = { ...gi[i], caption: e.target.value };
                          return { ...f, gallery_images: gi };
                        })}
                        className="border-b border-soft py-1 bg-transparent text-[11px] focus:outline-none focus:border-charcoal"
                        data-testid={`event-form-gallery-caption-${i}`}
                      />
                    </div>
                  ))}
                  {(form.gallery_images || []).length < 10 && (
                    <label className="w-20 h-20 border border-dashed border-soft flex flex-col items-center justify-center cursor-pointer text-muted2 hover:border-charcoal hover:text-charcoal text-[10px] gap-1 transition-colors" data-testid="event-form-gallery-upload-btn">
                      <Upload size={14}/> {uploadingGallery ? "…" : "Add"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { e.target.files?.[0] && uploadGalleryImage(e.target.files[0]); e.target.value = ""; }} data-testid="event-form-gallery-input"/>
                    </label>
                  )}
                </div>
                <div className="text-[10px] text-muted2 mt-1">Shown below the description as thumbnails that open full-screen (e.g. an artist's works for an exhibition). JPG/PNG/WebP, up to 8 MB each.</div>
              </div>

              {/* Catalogue download (PDF/DOC) */}
              <div className="md:col-span-2">
                <div className="overline mb-2">Catalogue (PDF / DOC, optional)</div>
                {form.catalog_url ? (
                  <div className="flex items-center gap-3 text-xs border border-soft bg-cream-2 px-3 py-2" data-testid="event-form-catalog-attached">
                    <span className="flex-1 truncate">{form.catalog_filename || "Catalogue"}</span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, catalog_url: "", catalog_filename: "" }))} className="text-muted2 hover:text-red-700" data-testid="event-form-catalog-remove" title="Remove"><X size={14}/></button>
                  </div>
                ) : (
                  <label className="btn-ghost inline-flex items-center gap-2 cursor-pointer text-xs" data-testid="event-form-catalog-upload-btn">
                    <Upload size={12} /> {uploadingCatalog ? "Uploading…" : "Upload catalogue"}
                    <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => { e.target.files?.[0] && uploadCatalog(e.target.files[0]); e.target.value = ""; }} data-testid="event-form-catalog-input"/>
                  </label>
                )}
                <div className="text-[10px] text-muted2 mt-1">Visitors see a “Download catalogue” button at the bottom of the event description. Max 15 MB.</div>
              </div>

              <div>
                <div className="overline mb-1">Fee (₹, flat total per booking)</div>
                <input type="number" min="0" placeholder="e.g. 850 · use 0 for free events" value={form.price_inr} onChange={(e)=>setForm({...form,price_inr:e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="event-form-price"/>
                <div className="text-[10px] text-muted2 mt-1">This is the flat amount charged per booking — for full-course events, enter the total course fee (not per-session). Payment methods live in your Razorpay dashboard.</div>
              </div>
              <div>
                <div className="overline mb-1">Capacity</div>
                <input type="number" min="1" placeholder="Max attendees" value={form.capacity} onChange={(e)=>setForm({...form,capacity:e.target.value})} className="w-full border-b border-soft py-2 bg-transparent"/>
              </div>
              <input placeholder="Starts at (ISO e.g. 2026-04-01T11:00:00+05:30)" value={form.starts_at} onChange={(e)=>setForm({...form,starts_at:e.target.value})} className="border-b border-soft py-2 bg-transparent md:col-span-2" data-testid="event-form-starts"/>
              <input placeholder="Ends at (optional ISO)" value={form.ends_at || ""} onChange={(e)=>setForm({...form,ends_at:e.target.value})} className="border-b border-soft py-2 bg-transparent md:col-span-2"/>
              <input placeholder="Location" value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})} className="border-b border-soft py-2 bg-transparent md:col-span-2"/>
              <input placeholder="Short description" value={form.short_description} onChange={(e)=>setForm({...form,short_description:e.target.value})} className="border-b border-soft py-2 bg-transparent md:col-span-2"/>
              <textarea placeholder="Full description" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} rows={4} className="border-b border-soft py-2 bg-transparent md:col-span-2"/>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.is_recurring} onChange={(e)=>setForm({...form,is_recurring:e.target.checked, booking_mode: e.target.checked ? "per_session" : "one_off"})}/> Recurring
              </label>
              <input placeholder="Recurrence label (e.g. Every Friday · 11 AM)" value={form.recurrence_label} onChange={(e)=>setForm({...form,recurrence_label:e.target.value})} className="border-b border-soft py-2 bg-transparent"/>

              {form.is_recurring && (
                <div className="md:col-span-2 bg-cream-2 border border-soft p-4 space-y-3">
                  <div className="overline">Booking mode</div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <label className="text-xs">
                      <div className="text-muted2 mb-1">Mode</div>
                      <select
                        value={form.booking_mode}
                        onChange={(e)=>setForm({...form,booking_mode:e.target.value})}
                        className="w-full border-b border-soft py-2 bg-transparent"
                        data-testid="event-form-booking-mode"
                      >
                        <option value="per_session">Per-session (drop-in)</option>
                        <option value="full_course">Full course (fixed batch)</option>
                      </select>
                    </label>
                    <label className="text-xs">
                      <div className="text-muted2 mb-1">Time (24h)</div>
                      <input
                        type="time"
                        value={form.recurrence_time}
                        onChange={(e)=>setForm({...form,recurrence_time:e.target.value})}
                        className="w-full border-b border-soft py-2 bg-transparent"
                        data-testid="event-form-time"
                      />
                    </label>
                    {form.booking_mode === "full_course" && (
                      <label className="text-xs">
                        <div className="text-muted2 mb-1">Total sessions</div>
                        <input
                          type="number" min="2" max="52"
                          value={form.session_count}
                          onChange={(e)=>setForm({...form,session_count:Number(e.target.value) || 1})}
                          className="w-full border-b border-soft py-2 bg-transparent"
                          data-testid="event-form-session-count"
                        />
                      </label>
                    )}
                  </div>

                  <div className="text-xs">
                    <div className="text-muted2 mb-2">Weekdays (tick all that apply)</div>
                    <div className="flex flex-wrap gap-2" data-testid="event-form-weekdays">
                      {WEEKDAY_OPTS.map(w => {
                        const active = (form.recurrence_weekdays || []).includes(w.v);
                        return (
                          <button
                            key={w.v}
                            type="button"
                            onClick={() => {
                              const cur = new Set(form.recurrence_weekdays || []);
                              if (cur.has(w.v)) cur.delete(w.v); else cur.add(w.v);
                              setForm({ ...form, recurrence_weekdays: [...cur].sort((a,b)=>a-b) });
                            }}
                            className={`px-3 py-1.5 border text-xs transition-colors ${active ? "" : "bg-transparent text-charcoal border-soft hover:border-charcoal"}`}
                            style={active ? { background: "#1C1C19", color: "#F9F8F6", borderColor: "#1C1C19" } : {}}
                            data-testid={`event-form-weekday-${w.l.toLowerCase()}`}
                          >
                            {w.l.slice(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="text-[11px] text-muted2">
                    {form.booking_mode === "per_session"
                      ? "Drop-in: buyer picks one session date from a calendar at checkout. Any ticked weekday is valid."
                      : `Course: buyer books the whole batch — ${form.session_count || 0} consecutive sessions across the ticked weekdays.`}
                  </div>
                </div>
              )}

              {/* Shared resource pool — e.g. all wheel events share 5 pottery wheels */}
              <div className="md:col-span-2">
                <div className="overline mb-1">Shared resource (optional)</div>
                <div className="flex items-center gap-3">
                  <select
                    value={form.resource_pool || ""}
                    onChange={(e) => setForm({ ...form, resource_pool: e.target.value })}
                    className="flex-1 border-b border-soft py-2 bg-transparent text-sm"
                    data-testid="event-form-resource-pool"
                  >
                    <option value="">— none —</option>
                    {pools.map(p => (
                      <option key={p.name} value={p.name}>{p.label} (cap {p.capacity})</option>
                    ))}
                  </select>
                  <Link to="/admin/resource-pools" className="text-xs link-hover whitespace-nowrap">Manage pools ↗</Link>
                </div>
                <div className="text-[10px] text-muted2 mt-1">Events sharing the same pool compete for the same slots on any given date + time (e.g. all wheel classes share 5 pottery wheels).</div>
              </div>
              <label className="flex items-center gap-2 text-xs md:col-span-2">
                <input type="checkbox" checked={form.is_published} onChange={(e)=>setForm({...form,is_published:e.target.checked})}/> Published
              </label>
              <label className="flex items-center gap-2 text-xs md:col-span-2" data-testid="event-form-featured-wrap">
                <input type="checkbox" checked={!!form.is_featured} onChange={(e)=>setForm({...form,is_featured:e.target.checked})} data-testid="event-form-featured"/>
                <span>Featured — floats to the top of listings and shows a “Highlight” chip on the editorial pages. Best used sparingly (1–2 events).</span>
              </label>
              <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="event-form-save">{editingId ? "Save changes" : "Create event"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {qrEvent && <EventQrModal event={qrEvent} onClose={() => setQrEvent(null)} />}
    </div>
  );
}
